import { Link } from "react-router-dom";
import { ArrowRight, Waves, Bike, Star, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { API } from "../lib/utils";
import Marquee from "../components/Marquee";

const HomePage = () => {
  const { t, lang } = useLang();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* ===== HERO ===== */}
      <section className="relative bg-white">
        <div className="container-flow grid grid-cols-1 lg:grid-cols-12 gap-10 pt-12 lg:pt-20 pb-20">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <p className="text-overline" data-testid="hero-overline">{t("home.overline")}</p>
            <h1 className="mt-6 hd-italic text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.9] text-forest" data-testid="hero-title">
              {t("home.title_l1")}<br />
              {t("home.title_l2")} <span className="text-burnt">{t("home.title_accent")}</span>.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              {t("home.subtitle")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/reservation" data-testid="hero-cta-primary" className="btn-primary">
                {t("home.cta_primary")} <ArrowRight size={18} />
              </Link>
              <Link to="/notre-histoire" data-testid="hero-cta-secondary" className="btn-secondary">
                {t("home.cta_secondary")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/1249545/pexels-photo-1249545.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Swimmer"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-8 -left-8 w-40 h-40 hidden md:block overflow-hidden border-4 border-white">
              <img
                src="https://images.unsplash.com/photo-1471506480208-91b3a4cc78be?crop=entropy&cs=srgb&fm=jpg&w=400&q=80"
                alt="Cyclist"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -top-4 -right-4 bg-burnt text-white px-5 py-3 font-heading italic font-black uppercase tracking-tight text-sm">
              EST. 2025
            </div>
          </div>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <Marquee items={["Natation", "Cyclisme", "Triathlon", "Performance", "Sur Mesure", "Coaching"]} />

      {/* ===== IDENTITY ===== */}
      <section className="bg-forest text-white section-pad">
        <div className="container-flow grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-overline !text-energy" data-testid="identity-overline">{t("home.identity_overline")}</p>
            <h2 className="mt-4 hd-italic text-5xl md:text-6xl leading-tight">{t("home.identity_title")}</h2>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl">{t("home.identity_body")}</p>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-white/20 pt-12">
              {[
                { n: "100+", l: t("home.stats.athletes") },
                { n: "2", l: t("home.stats.years") },
                { n: "98%", l: t("home.stats.rating") },
              ].map((s, i) => (
                <div key={i}>
                  <p className="font-heading font-black italic text-5xl md:text-6xl text-energy">{s.n}</p>
                  <p className="mt-2 text-sm uppercase tracking-widest text-white/70">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== KIDS SWIMMING ===== */}
      <section className="bg-white section-pad border-t border-border">
        <div className="container-flow grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1557469778-0b3269a1cc7a?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                alt="Cours de natation pour enfants en piscine"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-overline">{t("kids.overline")}</p>
            <h2 className="mt-4 hd-italic text-5xl md:text-6xl text-forest leading-tight">{t("kids.title")}</h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{t("kids.body")}</p>
            <ul className="mt-8 space-y-3">
              {[t("kids.bullet1"), t("kids.bullet2"), t("kids.bullet3"), t("kids.bullet4")].map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-base">
                  <span className="inline-block w-2 h-2 bg-burnt" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/natation-enfants" data-testid="kids-learn-more" className="btn-primary">
                {t("home.learn_more")} <ArrowRight size={18} />
              </Link>
              <Link to="/reservation" data-testid="kids-cta" className="btn-secondary">
                {t("kids.cta")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES BENTO ===== */}
      <section className="bg-white section-pad">
        <div className="container-flow">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mb-12">
            <div className="md:col-span-7">
              <p className="text-overline">{t("home.services_overline")}</p>
              <h2 className="mt-4 hd-italic text-5xl md:text-6xl text-forest leading-tight">{t("home.services_title")}</h2>
            </div>
            <Link to="/services" className="md:col-span-5 md:text-right font-heading uppercase tracking-widest text-sm font-bold text-burnt hover:text-energy inline-flex items-center gap-2 md:justify-end">
              {t("home.learn_more")} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div data-testid="service-swim-card" className="md:col-span-7 relative overflow-hidden group min-h-[460px] border border-border">
              <img
                src="https://images.pexels.com/photos/29280139/pexels-photo-29280139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"
                alt="Swimming"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-10 text-white">
                <Waves size={36} className="text-energy mb-4" />
                <h3 className="hd-italic text-5xl md:text-6xl">{t("home.service_swim_title")}</h3>
                <p className="mt-4 max-w-lg text-white/90 text-lg leading-relaxed">{t("home.service_swim_body")}</p>
              </div>
            </div>

            <div data-testid="service-cycle-card" className="md:col-span-5 relative overflow-hidden group min-h-[460px] border border-border">
              <img
                src="https://images.pexels.com/photos/18823753/pexels-photo-18823753.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
                alt="Cycling"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-burnt/90 via-burnt/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-10 text-white">
                <Bike size={36} className="text-white mb-4" />
                <h3 className="hd-italic text-5xl md:text-6xl">{t("home.service_cycle_title")}</h3>
                <p className="mt-4 text-white/95 text-lg leading-relaxed">{t("home.service_cycle_body")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="bg-bone section-pad">
        <div className="container-flow">
          <div className="max-w-3xl">
            <p className="text-overline">{t("home.testimonials_overline")}</p>
            <h2 className="mt-4 hd-italic text-5xl md:text-6xl text-forest leading-tight">{t("home.testimonials_title")}</h2>
          </div>
          <div className={`mt-14 grid grid-cols-1 ${testimonials.length > 1 ? "md:grid-cols-2" : "max-w-3xl"} gap-6`}>
            {testimonials.slice(0, 4).map((tm) => (
              <article
                key={tm.id}
                data-testid={`testimonial-${tm.id}`}
                className="bg-white p-10 border border-border hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="flex items-center gap-1 text-burnt">
                  {Array.from({ length: tm.rating }).map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" />
                  ))}
                </div>
                <p className="mt-5 font-heading italic text-2xl leading-snug text-forest">
                  « {lang === "fr" ? tm.quote_fr : tm.quote_en} »
                </p>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="font-heading uppercase tracking-widest text-sm font-bold text-forest">{tm.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">{lang === "fr" ? tm.role_fr : tm.role_en}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA STRIP ===== */}
      <section className="bg-burnt text-white section-pad">
        <div className="container-flow flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Zap size={28} className="text-white/80 mb-3" />
            <h2 className="hd-italic text-5xl md:text-6xl leading-tight">{lang === "fr" ? "Prêt à entrer dans le flow ?" : "Ready to enter the flow?"}</h2>
          </div>
          <Link to="/reservation" data-testid="cta-strip-book" className="btn-ghost !border-white !bg-transparent hover:!bg-white hover:!text-burnt">
            {t("nav.book_cta")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
